import {
  RowData,
  ColumnDef as TanStackColumnDef,
  Header,
  Cell,
  HeaderGroup,
  Row,
  PaginationState,
  ColumnSort,
  TableOptions,
  Table,
  SortingState
} from '@tanstack/react-table';
import { Placement } from '@floating-ui/dom';
import { ReactNode } from 'react';

export enum PinDirections {
  Left = 'left',
  Right = 'right'
}

export interface INaviTableTooltip {
  content: ReactNode;
  position?: Placement;
  hiddenPadding?: number;
}

export type NaviTableColumnDef<TData extends RowData, TValue = unknown> = TanStackColumnDef<
  TData,
  TValue
> & {
  accessorKey: string;
  pin?: PinDirections;
  tooltip?: boolean | ((cell: Cell<TData, TValue>) => INaviTableTooltip | null);
  headerTooltip?: boolean | ((header: Header<TData, any>) => INaviTableTooltip);
  cellStyle?: React.CSSProperties;
};

export enum PaginationOptions {
  CLIENT_SIDE = 'client',
  SERVER_SIDE = 'server'
}

export enum SortingOptions {
  CLIENT_SIDE = 'client',
  SERVER_SIDE = 'server'
}

export enum NaviTableVariants {
  DEFAULT = 'DEFAULT',
  CARDS = 'CARDS'
}

export enum NaviTableSortingDirections {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface INaviTable<TData extends RowData = RowData>
  extends Omit<TableOptions<TData>, 'onPaginationChange' | 'onSortingChange' | 'getCoreRowModel'> {
  data: TData[];
  columns: NaviTableColumnDef<TData>[];
  variant?: NaviTableVariants;
  tableName?: string;
  sizeColumnsToFit?: boolean;
  loading?: boolean;
  defaultColumnDef?: Partial<NaviTableColumnDef<TData>>;
  paginationType?: PaginationOptions;
  sortingType?: SortingOptions;
  defaultPaginationState?: PaginationState;
  defaultSortingState?: SortingState;
  headerClasses?: string;
  headerTopCoordinate?: number;
  totalElements?: number;
  tableClasses?: string;
  tableContainerClasses?: string;
  paginationClasses?: string;
  bodyRowStyles?: (row: TData) => string;
  alternateRowColor?: string;
  onPaginationChange?: (paginationState: PaginationState) => void;
  onSortingChange?: (sortingState: ColumnSort) => void;
  onRowClick?: (row: TData) => void;
  enableRowClick?: (row: TData) => boolean;
  expandedRowComponent?: (row: TData) => ReactNode;
  onCellClick?: (columnDef: NaviTableColumnDef<TData>) => void;
  emptyDataComponent?: ReactNode;
  showPaginationComponent?: boolean;
  doNotNavigate?: boolean;
}

export interface INaviTableHeader<TData extends RowData, TValue = unknown> {
  header: Header<TData, TValue>;
}

export interface INaviTableCell<TData extends RowData, TValue = unknown> {
  cell: Cell<TData, TValue>;
}

export interface INaviTableHeaderRow<TData extends RowData, TValue = unknown> {
  headerGroup: HeaderGroup<TData>;
}

export interface INaviTableBodyRow<TData extends RowData> {
  row: Row<TData>;
}

export interface INaviTableBodyRowWrapper<TData extends RowData> {
  loading?: boolean;
  rows: Row<TData>[];
  totalWidth: number;
  bodyRowStyles?: (row: TData) => string;
  alternateRowColor?: string;
  onRowClick?: (row: TData) => void;
  enableRowClick?: (row: TData) => boolean;
  onCellClick?: (columnDef: NaviTableColumnDef<TData>) => void;
}

export interface UseNaviTableProps<TData extends RowData> {
  table: Table<TData>;
  totalWidth: number;
  headerRef: React.RefObject<HTMLDivElement>;
  tableRef: React.RefObject<HTMLDivElement>;
  headerLineRef: React.RefObject<HTMLDivElement>;
  isScrolled: boolean;
  rerender: () => void;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
}

export interface NaviTableBodyCellValueProps {
  isCellValueString: boolean;
  cellValue: React.ReactNode | JSX.Element;
  className: string;
}
